import "./ui/styles.css";

import { BlinkDetector, type DetectorTick } from "./core/blinkDetector.ts";
import { EventBus } from "./core/eventBus.ts";
import { SessionMetricsTracker } from "./core/metrics.ts";
import { Recorder, serializeRecording } from "./core/recorder.ts";
import { SuppressionTracker } from "./core/suppression.ts";
import {
  DEFAULT_CONFIG,
  type DetectorConfig,
  type FilterKind,
  type SignalFrame,
  type SignalKind,
} from "./core/types.ts";
import { Balance } from "./game/balance.ts";
import { Blackout } from "./game/blackout.ts";
import { FatigueBlur } from "./game/fatigueBlur.ts";
import { type AppMode, PointerChannel } from "./game/input.ts";
import { Kart } from "./game/kart.ts";
import { Lantern } from "./game/lantern.ts";
import { MECHANIC_IDS, type Mechanic, type MechanicId, NullMechanic } from "./game/mechanic.ts";
import { PoseArrow, PoseNeutral } from "./game/poseArrow.ts";
import { Reload } from "./game/reload.ts";
import { Tempo } from "./game/tempo.ts";
import { CameraFrameSource } from "./sources/CameraFrameSource.ts";
import { SignalChart } from "./ui/chart.ts";
import { ClosureHistory } from "./ui/closureHistory.ts";
import { Metronome } from "./ui/metronome.ts";
import { Overlay } from "./ui/overlay.ts";
import { EventLog, explainSilence, LogGroup, Readouts } from "./ui/panel.ts";

// 24, not the spec's 15. At 15fps a 220ms blink is 3-4 samples and none is
// likely to land on the peak, so shallow blinks are under-measured below the
// detection threshold and vanish entirely — see the capture-rate tests. The
// battery argument for 15 still stands; the signal-quality argument does not.
const TARGET_FPS = 24;
const METRONOME_INTERVAL_MS = 3000;
/** Three presses inside this window switch mode instead of acting. */
const TRIPLE_TAP_MS = 450;
/** Rows kept in the on-camera terminal. More than this covers the face. */
const TERMINAL_ROWS = 5;

const el = <T extends HTMLElement>(id: string): T => {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing #${id}`);
  return found as T;
};

const video = el<HTMLVideoElement>("video");
const overlay = new Overlay(el<HTMLCanvasElement>("overlay"));
const readouts = new Readouts(el("readouts"));
const log = new LogGroup([new EventLog(el("log")), new EventLog(el("terminal"), TERMINAL_ROWS)]);
const status = el("stage-status");
const recIndicator = el("rec-indicator");
const controls = el("controls");
const trailTop = el("trail-top");
const trailNotes = el("trail-notes");
const trailFoot = el("trail-bottom");
const notes = el<HTMLDetailsElement>("notes");
const diagnosis = el("diagnosis");
const hud = {
  blinks: el("hud-blinks"),
  rate: el("hud-rate"),
  gap: el("hud-gap"),
  rec: el("hud-rec"),
  recCell: el("hud-rec").parentElement as HTMLElement,
  closed: el("hud-closed"),
  mechanic: el("hud-mechanic"),
  mechanicLabel: el("hud-mechanic-label"),
  mechanicCell: el("hud-mechanic-cell"),
};
const closures = new ClosureHistory(el("closures"));
const videoWrap = video.parentElement as HTMLElement;
const tapSurface = el<HTMLButtonElement>("tap-surface");
const tapFlash = el("tap-flash");
const tapHint = el("tap-hint");
const arrow = new PoseArrow(el<HTMLCanvasElement>("arrow"));
const poseNeutral = new PoseNeutral();

let config: DetectorConfig = { ...DEFAULT_CONFIG };

const bus = new EventBus();
const detector = new BlinkDetector(config, bus);
const metrics = new SessionMetricsTracker();
const recorder = new Recorder();
const chart = new SignalChart(el("chart"), config, TARGET_FPS);
const suppression = new SuppressionTracker(metrics, config.completeAmplitude);
const pointer = new PointerChannel(bus, tapSurface, () => lastFrame?.t ?? 0);

/**
 * Every mechanic, built once and swapped by reference.
 *
 * No subscribe/unsubscribe lifecycle, so there is nothing to leak and no order
 * of operations to get wrong. Only FatigueBlur is given the stage element —
 * the others own their own surfaces, so two mechanics can never contend for
 * the same inline style.
 *
 * `satisfies` rather than an annotation: with `noUncheckedIndexedAccess`, a
 * string-keyed Record would make every lookup `Mechanic | undefined`.
 */
const mechanics = {
  none: new NullMechanic(),
  fatigue: new FatigueBlur(videoWrap),
  reload: new Reload(el("mech-reload")),
  blackout: new Blackout(el("mech-marker")),
  balance: new Balance(el("balance-marker")),
  lantern: new Lantern(el("mech-lantern")),
  tempo: new Tempo(el("tempo-pip")),
  kart: new Kart(el("kart-road"), el("kart-arrow")),
} satisfies Record<MechanicId, Mechanic>;

let mechanic: Mechanic = mechanics.none;
/** What the triple-tap gesture returns to. Never `none`, or play does nothing. */
let lastPlayed: MechanicId = "fatigue";

/**
 * Debug mode is the harness; play mode is the mechanic. The same full-screen
 * surface serves both — labelling while recording, acting while playing — which
 * is what makes it worth having at all.
 */
let mode: AppMode = "debug";
/** Mode and mechanic as they were when Record was pressed. See toggleRecording. */
let recordedMode: AppMode = "debug";
let recordedMechanic: MechanicId = "none";
let suppressionValue = 0;
/** Every press, in either mode. Visible proof that input is landing at all. */
let taps = 0;

let camera: CameraFrameSource | null = null;
let lastTick: DetectorTick | null = null;
let lastFrame: SignalFrame | null = null;
let fps = 0;
let lastFrameWallTime = 0;

bus.onAny((event) => {
  log.add(event);
  if (event.type === "blink") {
    metrics.add(event);
    suppression.onBlink(event);
    closures.add(event);
    // After the trackers, deliberately: EventBus runs typed handlers before
    // onAny ones, so a mechanic that subscribed directly would see metrics and
    // suppression in their pre-update state. Dispatching from here makes the
    // ordering explicit instead of incidental.
    if (mode === "play") mechanic.onBlink?.(event);
  }
  if (event.type === "suppression") {
    suppressionValue = event.value;
    if (mode === "play") mechanic.onSuppression?.(event);
  }
  if (event.type === "pointer") {
    if (recorder.isRecording) recorder.input(event);

    // Drags and releases go straight to the mechanic. They are not taps: they
    // do not label, do not flash and do not feed the mode gesture, but a
    // steering control is nothing without them.
    if (event.phase !== "down") {
      if (mode === "play") mechanic.onPointer?.(event);
      return;
    }

    // Wall time, not frame time: the gesture has to work with the camera off,
    // and frame time is 0 until the first frame arrives.
    if (registerTripleTap(performance.now())) return;

    taps++;
    if (mode === "play") {
      flashTap();
      mechanic.onPointer?.(event);
      // No per-press log line. A mechanic with a fire button produces two or
      // three presses a second, which would flush the five-row terminal
      // continuously. The flash confirms the press; hud-rec counts them.
    } else {
      labelBlink();
    }
  }
});

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

const button = (label: string, onClick: () => void, className = "", parent = controls) => {
  const b = document.createElement("button");
  b.textContent = label;
  b.className = className;
  b.addEventListener("click", onClick);
  parent.append(b);
  return b;
};

const select = <T extends string>(
  label: string,
  options: readonly T[],
  initial: T,
  onChange: (value: T) => void,
  parent = controls,
) => {
  const wrap = document.createElement("label");
  wrap.className = "field";
  wrap.textContent = label;
  const s = document.createElement("select");
  for (const option of options) {
    const o = document.createElement("option");
    o.value = option;
    o.textContent = option;
    s.append(o);
  }
  s.value = initial;
  s.addEventListener("change", () => onChange(s.value as T));
  wrap.append(s);
  parent.append(wrap);
  return s;
};

// The two actions a session actually needs, side by side and nothing between
// them. Everything else moved to the trail, where it is read rather than used.
const cameraButton = button("Start camera", () => void toggleCamera(), "primary");
const recordButton = button("Record", () => toggleRecording());
recordButton.disabled = true;

/**
 * Deliberately not persisted.
 *
 * `lub.autoUpload` and `lub.notesOpen` are preferences and should survive a
 * reload. A mechanic is a per-session experiment, and remembering it means the
 * harness opens in play mode with labelling silently off — which is the exact
 * mechanism that produced seven consecutive label-less sessions. Every reload
 * starts at `none`, in debug, measuring.
 *
 * Built with the shared `select` helper so it lands inside `label.field`, which
 * `PointerChannel.#isControl` already excludes — touching the picker cannot
 * fire the game verb or feed the triple-tap gesture.
 */
const mechanicSelect = select<MechanicId>(
  "mechanic",
  MECHANIC_IDS,
  "none",
  (id) => chooseMechanic(id),
  el("mechanic-row"),
);

/**
 * On by default now, which is a reversal worth stating.
 *
 * It used to be off, on the grounds that a default where data leaves the device
 * unless you stop it is hard to walk back. That reasoning assumed a manual
 * Upload button existed as the normal path. It no longer does — so an off
 * default does not mean "you chose not to upload", it means every session is
 * silently discarded on the walk back to the desk. The toggle is still here and
 * still remembered; only the default moved.
 */
const AUTO_UPLOAD_KEY = "lub.autoUpload";
const storedAutoUpload = localStorage.getItem(AUTO_UPLOAD_KEY);
let autoUpload = storedAutoUpload === null ? true : storedAutoUpload === "1";

const autoButton = button(
  "",
  () => {
    autoUpload = !autoUpload;
    localStorage.setItem(AUTO_UPLOAD_KEY, autoUpload ? "1" : "0");
    paintAutoButton();
  },
  "",
  trailFoot,
);

function paintAutoButton(): void {
  autoButton.textContent = autoUpload ? "Auto-upload: on" : "Auto-upload: off";
  autoButton.classList.toggle("primary", autoUpload);
}
paintAutoButton();

const metronome = new Metronome(METRONOME_INTERVAL_MS, () => {
  // The cue time is the ground truth, not the moment the subject reacts to it.
  if (lastFrame) recorder.label(lastFrame.t, "metronome");
});
const metronomeButton = button(
  "Metronome",
  () => {
    if (metronome.running) {
      metronome.stop();
      metronomeButton.classList.remove("armed");
    } else {
      metronome.start();
      metronomeButton.classList.add("armed");
    }
  },
  "",
  trailNotes,
);

select<SignalKind>(
  "signal",
  ["blendshape", "ear"],
  config.signal,
  (signal) => applyConfig({ ...config, signal }),
  trailTop,
);

select<FilterKind>(
  "filter",
  ["one-euro", "moving-average", "median", "none"],
  config.filter.kind,
  (kind) => applyConfig({ ...config, filter: { ...config.filter, kind } }),
  trailTop,
);

const hint = document.createElement("div");
hint.className = "hint";
hint.innerHTML =
  "<b>Record</b>, then tap <b>anywhere on the screen</b> every time you blink, " +
  "then <b>Stop</b>. Sessions upload themselves unless you switch that off below." +
  "<br><b>Triple-tap</b> anywhere to swap between debug (taps label blinks) and " +
  "play (taps jump). On a keyboard: <kbd>R</kbd> record, <kbd>Space</kbd> label, " +
  "<kbd>E</kbd> export a file, <kbd>C</kbd> clear.<br>" +
  "Recordings hold derived signals only — never pixels. Drop one into " +
  "<code>fixtures/</code> and <code>npm run eval</code> scores the detector against it.";
trailNotes.append(hint);

/**
 * Everything you read rather than drive lives behind one disclosure, and it
 * remembers whether you left it open.
 *
 * Collapsed, the log measures zero in every dimension, so its follow-the-tail
 * check cannot tell "pinned to the bottom" from "scrolled away" and it reopens
 * on its oldest rows. Reopening has to scroll it explicitly.
 */
const NOTES_KEY = "lub.notesOpen";
notes.open = localStorage.getItem(NOTES_KEY) === "1";
if (notes.open) log.scrollToEnd();

notes.addEventListener("toggle", () => {
  localStorage.setItem(NOTES_KEY, notes.open ? "1" : "0");
  if (notes.open) log.scrollToEnd();
});

/** Presses inside the triple-tap window, newest last. */
let tapWindow: number[] = [];

/**
 * The mode switch as a gesture — now with the two cases where it must not fire.
 *
 * It was firing accidentally, and not rarely. Counting three-taps-inside-450ms
 * in the committed fixtures: `real-15fps-before-throttle-fix` contains 6 and
 * `real-24fps-tapping-fast` contains 8, with tap gaps down to 125ms. A human
 * labelling their own blinks taps that fast routinely — `recorder.ts` already
 * documents 34 of 76 taps under 300ms apart — so the gesture was silently
 * switching debug recordings into play and stopping labelling for the rest of
 * the session. That is indistinguishable, from the outside, from yet another
 * session that came back with too few labels.
 *
 * So it is disabled whenever rapid tapping is the legitimate thing to be
 * doing: while recording in debug, and while a mechanic is running. The picker
 * is the way out in both cases, which is why it earns a permanent row.
 *
 * @returns true if this press was consumed by the gesture.
 */
function registerTripleTap(now: number): boolean {
  if (recorder.isRecording && mode === "debug") return false;
  if (mechanic.id !== "none") return false;

  tapWindow = tapWindow.filter((t) => now - t < TRIPLE_TAP_MS);
  tapWindow.push(now);
  if (tapWindow.length < 3) return false;

  tapWindow = [];
  // Entering play must land on a real mechanic, or the gesture produces a mode
  // in which nothing happens — a regression from what it used to do.
  chooseMechanic(mode === "debug" ? lastPlayed : "none");
  return true;
}

/**
 * The one control that decides both what plays and what a tap means.
 *
 * Mode and mechanic are collapsed on purpose. A mechanic is inert in debug —
 * every callback is gated on play — so keeping them independent would produce a
 * state where you pick a mechanic and nothing happens, which reads as broken.
 */
function chooseMechanic(id: MechanicId): void {
  // Reset on both sides of the swap. The outgoing one to clear the styles it
  // owns, the incoming one so it never starts mid-session with stale state.
  mechanic.reset();
  mechanic = mechanics[id];
  mechanic.reset();

  if (id !== "none") lastPlayed = id;
  mechanicSelect.value = id;
  videoWrap.dataset.mechanic = id;
  setMode(id === "none" ? "debug" : "play");
  recorder.noteMode(lastFrame?.t ?? 0, mode, mechanic.id);
}

/**
 * Switches what the screen-filling surface means.
 *
 * Debug is the measurement harness; play is the mechanic. Keeping one surface
 * and changing its meaning avoids halving the target area for both — and the
 * whole reason the surface exists is that a smaller target produced seven
 * consecutive sessions with zero labels.
 */
function setMode(next: AppMode): void {
  const changed = mode !== next;
  mode = next;
  tapHint.textContent = mode === "play" ? mechanic.hint : "tap anywhere on every blink";
  if (changed) log.write(lastFrame?.t ?? 0, `mode → ${mode} · ${mechanic.id}`, "log-info");

  // Entering play mid-recording is not blocked, but it must be said out loud:
  // taps stop being labels, and a session with no labels can show the signal
  // and cannot score accuracy.
  if (changed && mode === "play" && recorder.isRecording) {
    log.write(
      lastFrame?.t ?? 0,
      "play mode — taps no longer label blinks, so this recording will score nothing",
      "log-alert",
    );
  }

  // Input is captured document-wide in both modes, so a press anywhere counts.
  // The surface stays as a visible hint of where the action is.
  pointer.attach();
  tapSurface.hidden = mode === "play" ? false : !recorder.isRecording;
  recorder.noteMode(lastFrame?.t ?? 0, mode, mechanic.id);
}

/**
 * Confirms a press landed, in every mode and whether or not it did anything.
 *
 * Restarting the animation needs the class removed and a reflow forced, or a
 * second tap inside the animation window does nothing visible — the common case
 * when presses come in quick succession.
 */
function flashTap(): void {
  tapFlash.classList.remove("lit");
  void tapFlash.offsetWidth;
  tapFlash.classList.add("lit");
}

function labelBlink(): void {
  if (!recorder.isRecording || !lastFrame) {
    log.write(lastFrame?.t ?? 0, `tap ${taps} · not recording, so not labelled`, "log-info");
    return;
  }
  const accepted = recorder.label(lastFrame.t, "keytap");
  if (accepted) {
    log.write(lastFrame.t, `tap ${taps} · labelled`, "log-info");
    flashTap();
  } else {
    log.write(lastFrame.t, `tap ${taps} · too soon after the last, ignored`, "log-alert");
  }
}

function applyConfig(next: DetectorConfig): void {
  config = next;
  detector.reconfigure(config);
  metrics.reset();
  closures.clear();
  chart.reset();
  chart.refresh(config, TARGET_FPS);
  log.write(lastFrame?.t ?? 0, `config → ${config.signal} / ${config.filter.kind}`, "log-info");
}

// ---------------------------------------------------------------------------
// Camera lifecycle
// ---------------------------------------------------------------------------

async function toggleCamera(): Promise<void> {
  if (camera) {
    await camera.stop();
    camera = null;
    metronome.stop();
    metronomeButton.classList.remove("armed");
    if (recorder.isRecording) toggleRecording();
    cameraButton.textContent = "Start camera";
    recordButton.disabled = true;
    recIndicator.hidden = true;
    status.hidden = false;
    status.textContent = "Camera off";
    overlay.clear();
    arrow.clear();
    mechanic.reset();
    closures.clear();
    return;
  }

  cameraButton.disabled = true;
  status.textContent = "Starting camera…";
  status.hidden = false;

  try {
    camera = new CameraFrameSource({
      video,
      targetFps: TARGET_FPS,
      onDiagnostic: (message) => log.write(lastFrame?.t ?? 0, message, "log-alert"),
    });
    camera.onFrame(onFrame);
    await camera.start();

    const info = camera.info;
    overlay.resize(info?.width ?? 1280, info?.height ?? 720);
    arrow.resize(info?.width ?? 1280, info?.height ?? 720);
    // A phone front camera in portrait is not 16:9. Take the ratio from the
    // stream instead of guessing, or the frame gets letterboxed into a sliver.
    if (info?.width && info?.height) {
      videoWrap.style.aspectRatio = `${info.width} / ${info.height}`;
    }
    status.hidden = true;
    recIndicator.hidden = false;
    cameraButton.textContent = "Stop camera";
    recordButton.disabled = false;
    detector.reset();
    metrics.reset();
    suppression.reset();
    poseNeutral.reset();
    mechanic.reset();
    closures.clear();
    chart.reset();
    log.write(
      0,
      `camera ${info?.width}×${info?.height} → infer ${info?.inferenceSize} · ` +
        `${info?.delegate} delegate · target ${TARGET_FPS}fps`,
      "log-info",
    );
  } catch (err) {
    camera = null;
    status.textContent = `Camera failed: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    cameraButton.disabled = false;
  }
}

function onFrame(frame: SignalFrame): void {
  lastFrame = frame;

  const now = performance.now();
  if (lastFrameWallTime > 0) {
    const instant = 1000 / Math.max(now - lastFrameWallTime, 1);
    fps = fps === 0 ? instant : fps * 0.9 + instant * 0.1;
  }
  lastFrameWallTime = now;

  const tick = detector.push(frame);
  lastTick = tick;
  metrics.tick(frame.t, frame.faceFound && tick.closure >= config.closeThreshold);
  recorder.push(frame);

  const level = suppression.tick(frame.t);
  if (level) bus.emit(level);

  overlay.draw(frame, tick.closure, tick.lowConfidence);

  // The steering channel, drawn relative to a learned neutral. Every recorded
  // session held the phone below eye level, so absolute pose points down.
  const deflection = poseNeutral.push(frame.pose);
  if (frame.faceFound) arrow.draw(deflection, poseNeutral.settled);
  else arrow.clear();

  chart.push(frame.t, tick.closureRaw, tick.closure);
  paintHud();
}

/**
 * Painted from the frame handler, not only from the animation frame loop.
 *
 * The counters previously updated solely inside a requestAnimationFrame chain
 * that reschedules itself at the end of its own body — so a single throw
 * anywhere in it killed every number on the page permanently while the
 * event-driven log carried on, which reads exactly like "the log works and
 * nothing else does". The loop is now fault-tolerant too, but driving the HUD
 * from the frame path means it cannot be starved by rendering at all.
 */
function paintHud(): void {
  const m = metrics.snapshot();
  setText(hud.blinks, String(m.totalComplete));
  setText(hud.rate, m.ratePerMinute.toFixed(1));
  setText(hud.gap, `${(m.sinceLastBlinkMs / 1000).toFixed(1)}s`);
  setText(
    hud.rec,
    recorder.isRecording
      ? `${(recorder.durationMs / 1000).toFixed(0)}s · ${recorder.labelCount}`
      : taps > 0
        ? `${taps} taps`
        : "—",
  );
  hud.recCell.classList.toggle("armed", recorder.isRecording);
  setText(hud.closed, `${(m.closedMs / 1000).toFixed(1)}s`);

  // The mechanic swaps in for the recording cell rather than adding a sixth.
  // Five cells already crowd a phone, and while a mechanic is running its state
  // IS the proof input is landing — which is all the tap counter was for.
  const readout = mode === "play" ? mechanic.readout() : null;
  hud.mechanicCell.hidden = readout === null;
  hud.recCell.hidden = readout !== null;
  if (readout) {
    setText(hud.mechanic, readout.value);
    setText(hud.mechanicLabel, readout.label);
    hud.mechanicCell.classList.toggle("armed", readout.alert === true);
  }
}

function setText(node: HTMLElement, value: string): void {
  if (node.textContent !== value) node.textContent = value;
}

// ---------------------------------------------------------------------------
// Recording
// ---------------------------------------------------------------------------

function toggleRecording(): void {
  if (recorder.isRecording) {
    recorder.stop();
    recordButton.textContent = "Record";
    recordButton.classList.remove("armed");
    if (mode === "debug") tapSurface.hidden = true;
    log.write(
      lastFrame?.t ?? 0,
      `recorded ${recorder.frameCount} frames · ${recorder.labelCount} labels`,
      "log-info",
    );
    if (recorder.labelCount === 0 && recorder.frameCount > 0) {
      // Naming the reason matters more than naming the symptom. A play session
      // has no labels BY DESIGN, and reporting that as a bare warning is what
      // makes a working session look like a broken one.
      const labelling = recorder.labellingMs(lastFrame?.t ?? 0);
      log.write(
        lastFrame?.t ?? 0,
        labelling < recorder.durationMs * 0.5
          ? `no labels — taps were the game verb for ${((recorder.durationMs - labelling) / 1000).toFixed(0)}s of ${(recorder.durationMs / 1000).toFixed(0)}s. Expected in play mode.`
          : "no labels in this session — it can show the signal but cannot score accuracy",
        "log-alert",
      );
    }
    if (recorder.frameCount > 0) {
      // With no Upload button left, an off switch means the session is gone the
      // moment you record over it. Say so rather than failing quietly.
      if (autoUpload) void uploadRecording();
      else
        log.write(
          lastFrame?.t ?? 0,
          "auto-upload is off — this session is not saved anywhere. Press E to download it.",
          "log-alert",
        );
    }
    return;
  }
  recorder.start();
  // Snapshotted here, not read at build time. buildRecording() runs when you
  // press Stop, and a session whose mode changed halfway through would
  // otherwise be filed under whatever it happened to end in. Every LATER
  // change lands in the modeChanges track — a snapshot alone proved unable to
  // explain a session that switched partway.
  recordedMode = mode;
  recordedMechanic = mechanic.id;
  recorder.noteMode(lastFrame?.t ?? 0, mode, mechanic.id);
  recordButton.textContent = "Stop recording";
  recordButton.classList.add("armed");
  log.write(lastFrame?.t ?? 0, "recording started — tap anywhere on every blink", "log-info");
}

const CONDITIONS_KEY = "lub.conditions";

/** A prefilled guess beats an empty box: every session so far recorded none. */
function defaultConditions(): string {
  const parts = [/iPhone|iPad|Android/.test(navigator.userAgent) ? "phone" : "laptop"];
  const info = camera?.info;
  if (info) parts.push(`${Math.round(info.inferenceMs)}ms-infer`);
  return parts.join(", ");
}

function buildRecording(ask = true): string | null {
  const remembered = localStorage.getItem(CONDITIONS_KEY) || defaultConditions();
  let conditions = remembered;

  if (ask) {
    const answer = prompt(
      "Capture conditions, comma separated (e.g. glasses, backlit, indoor):",
      remembered,
    );
    if (answer === null) return null;
    conditions = answer;
    localStorage.setItem(CONDITIONS_KEY, answer);
  }

  const info = camera?.info;
  return serializeRecording(
    recorder.build(
      {
        fps: TARGET_FPS,
        resolution: `${info?.width ?? 0}x${info?.height ?? 0}`,
        conditions: conditions
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
        inferenceSize: info?.inferenceSize ?? "",
        inferenceMs: info ? Number(info.inferenceMs.toFixed(1)) : 0,
        sourceFps: info && Number.isFinite(info.sourceFps) ? Number(info.sourceFps.toFixed(1)) : 0,
        detector: {
          signal: config.signal,
          filter: config.filter.kind,
          closeThreshold: config.closeThreshold,
          completeAmplitude: config.completeAmplitude,
        },
        mode: recordedMode,
        mechanic: recordedMechanic,
      },
      navigator.userAgent,
    ),
  );
}

/**
 * Sends the session to /api/sessions — our own origin, so the CSP's
 * `connect-src 'self'` is untouched and the page still cannot reach any third
 * party. The payload is derived signal: no pixels exist to send.
 */
async function uploadRecording(): Promise<void> {
  // Never prompts. It runs unattended when a recording stops, and a modal that
  // appears while you are putting the phone down is a modal nobody reads.
  const payload = buildRecording(false);
  if (payload === null) return;

  log.write(lastFrame?.t ?? 0, "uploading session…", "log-info");
  try {
    const res = await fetch("/api/sessions", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
    });
    const body = (await res.json()) as { key?: string; error?: string };
    if (!res.ok) throw new Error(body.error ?? `${res.status}`);

    log.write(lastFrame?.t ?? 0, `uploaded as ${body.key}`, "log-blink");
  } catch (err) {
    log.write(
      lastFrame?.t ?? 0,
      `upload failed: ${err instanceof Error ? err.message : String(err)}`,
      "log-alert",
    );
  }
}

/** No button any more — <kbd>E</kbd> on a keyboard, for the offline case. */
function exportRecording(): void {
  if (recorder.frameCount === 0) return;
  const payload = buildRecording();
  if (payload === null) return;

  const blob = new Blob([payload], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${new Date().toISOString().replace(/[:.]/g, "-")}.blink.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;

  if (e.code === "Space") {
    e.preventDefault();
    labelBlink();
    return;
  }
  if (e.key === "r" || e.key === "R") {
    if (!recordButton.disabled) toggleRecording();
  }
  if (e.key === "e" || e.key === "E") exportRecording();
  if (e.key === "c" || e.key === "C") log.clear();
});

// Spec §5: stop inferring when the tab is hidden. The camera indicator staying
// lit while nothing is being computed is both a battery cost and a bad look.
document.addEventListener("visibilitychange", () => {
  if (document.hidden && camera && !recorder.isRecording) void toggleCamera();
});

let renderErrorReported = false;

/**
 * Always reschedules, even if the body throws.
 *
 * The previous version put requestAnimationFrame at the end of its own body, so
 * one exception stopped every readout on the page forever — and silently,
 * because the event-driven log kept updating and made it look like rendering
 * was fine. A UI loop that can die and take the instrumentation with it is a
 * bad trade at any error rate.
 */
function renderLoop(): void {
  try {
    renderFrame();
  } catch (err) {
    if (!renderErrorReported) {
      renderErrorReported = true;
      console.error("[render]", err);
      log.write(
        lastFrame?.t ?? 0,
        `render error: ${err instanceof Error ? err.message : String(err)}`,
        "log-alert",
      );
    }
  }
  requestAnimationFrame(renderLoop);
}

function renderFrame(): void {
  paintHud();
  const verdict = explainSilence(detector.stats, lastTick?.ready ?? false, config.closeThreshold);
  if (diagnosis.textContent !== verdict.text) diagnosis.textContent = verdict.text;
  diagnosis.className = `diagnosis${verdict.tone ? ` ${verdict.tone}` : ""}`;

  readouts.render({
    fps,
    metrics: metrics.snapshot(),
    closure: lastTick?.closure ?? Number.NaN,
    yawDegrees: ((lastFrame?.pose.yaw ?? 0) * 180) / Math.PI,
    ipd: lastFrame?.ipd ?? Number.NaN,
    delegate: camera?.info?.delegate ?? "—",
    inferenceMs: camera?.info?.inferenceMs ?? 0,
    inferenceSize: camera?.info?.inferenceSize ?? "",
    sourceFps: camera?.info?.sourceFps ?? Number.NaN,
    suppression: suppressionValue,
    lowConfidence: lastTick?.lowConfidence ?? false,
    recording: {
      active: recorder.isRecording,
      frames: recorder.frameCount,
      labels: recorder.labelCount,
    },
  });

  // Last, and quarantined. The instrumentation above is the thing that must
  // never stop; a mechanic is the newest and least-proven code on the page, and
  // when it used to run before `readouts.render` a throw inside it froze the
  // diagnosis and every stat while the HUD carried on ticking from the frame
  // path — the confusing partial failure the loop's try/catch exists to avoid.
  // A bad mechanic now disables itself and says so.
  if (mode === "play") {
    try {
      mechanic.update(performance.now());
    } catch (err) {
      log.write(
        lastFrame?.t ?? 0,
        `${mechanic.id} failed and was switched off: ${err instanceof Error ? err.message : String(err)}`,
        "log-alert",
      );
      chooseMechanic("none");
    }
  }
}

setMode("debug");
renderLoop();
